import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Select, InputNumber, Input, Switch, Descriptions, Steps, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SwapOutlined, CheckCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useBOMOperations,
  useCreateBOMOperation,
  useUpdateBOMOperation,
  useDeleteBOMOperation,
  useResequenceBOMOperations,
  useBOMTotalTime,
  useBOMQualityCheckpoints,
} from '@/hooks/useManufacturingPhase2';
import { useOperations } from '@/hooks/useManufacturingPhase2';
import { useWorkstations } from '@/hooks/useManufacturing';
import { BOMOperation, QualityCheckpointType } from '@/services/manufacturing-phase2.service';

const BOMRoutingPage: React.FC = () => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOperation, setEditingOperation] = useState<BOMOperation | null>(null);
  const [selectedBOM, setSelectedBOM] = useState<string>(''); // In real app, get from route params or selector
  const [quantity, setQuantity] = useState(100);

  // Mock BOM selector - in real app this would be from route params or a selector
  const bomOptions = [
    { id: 'bom-1', label: 'BOM-001 - Product A' },
    { id: 'bom-2', label: 'BOM-002 - Product B' },
  ];

  // API hooks
  const { data: routing = [], isLoading } = useBOMOperations(selectedBOM);
  const { data: operations = [] } = useOperations({ active_only: true });
  const { data: workstations = [] } = useWorkstations();
  const { data: totalTime } = useBOMTotalTime(selectedBOM, quantity);
  const { data: qualityCheckpoints = [] } = useBOMQualityCheckpoints(selectedBOM);
  const createOperation = useCreateBOMOperation();
  const updateOperation = useUpdateBOMOperation();
  const deleteOperation = useDeleteBOMOperation();
  const resequence = useResequenceBOMOperations();

  const handleCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      bom_id: selectedBOM,
      sequence_number: (routing.length || 0) + 10,
      batch_size: 1,
      requires_quality_check: false,
      is_active: true,
    });
    setEditingOperation(null);
    setModalVisible(true);
  };

  const handleEdit = (record: BOMOperation) => {
    form.setFieldsValue(record);
    setEditingOperation(record);
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Delete Routing Operation',
      content: 'Are you sure you want to delete this operation from the routing?',
      onOk: () => deleteOperation.mutate(id),
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingOperation) {
        await updateOperation.mutateAsync({ id: editingOperation.id, data: values });
      } else {
        await createOperation.mutateAsync(values);
      }
      
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const columns: ColumnsType<BOMOperation> = [
    {
      title: 'Seq',
      dataIndex: 'sequence_number',
      key: 'sequence_number',
      width: 70,
      sorter: (a, b) => a.sequence_number - b.sequence_number,
    },
    {
      title: 'Operation',
      dataIndex: ['operation', 'operation_name'],
      key: 'operation_name',
      width: 200,
      render: (_, record) => (
        <div>
          <div><strong>{record.operation?.operation_name || 'Unknown'}</strong></div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.operation?.operation_code}
          </div>
        </div>
      ),
    },
    {
      title: 'Workstation',
      dataIndex: ['workstation', 'workstation_name'],
      key: 'workstation',
      width: 150,
      render: (name) => name || <Tag>Not Assigned</Tag>,
    },
    {
      title: 'Setup (hrs)',
      dataIndex: 'setup_time_hours',
      key: 'setup_time_hours',
      width: 110,
      align: 'right',
      render: (val: number) => val.toFixed(2),
    },
    {
      title: 'Run (hrs/unit)',
      dataIndex: 'run_time_per_unit_hours',
      key: 'run_time_per_unit_hours',
      width: 130,
      align: 'right',
      render: (val: number) => val.toFixed(4),
    },
    {
      title: 'Teardown (hrs)',
      dataIndex: 'teardown_time_hours',
      key: 'teardown_time_hours',
      width: 120,
      align: 'right',
      render: (val: number) => val.toFixed(2),
    },
    {
      title: 'Quality Check',
      dataIndex: 'requires_quality_check',
      key: 'requires_quality_check',
      width: 130,
      render: (required: boolean, record) => (
        required ? (
          <Tag color="gold" icon={<CheckCircleOutlined />}>
            {record.quality_checkpoint_type || 'Required'}
          </Tag>
        ) : (
          <Tag>No</Tag>
        )
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="BOM Routing"
        extra={
          <Space>
            <Select
              placeholder="Select BOM"
              style={{ width: 250 }}
              value={selectedBOM || undefined}
              onChange={setSelectedBOM}
              options={bomOptions.map(b => ({ label: b.label, value: b.id }))}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} disabled={!selectedBOM}>
              Add Operation
            </Button>
          </Space>
        }
      >
        {selectedBOM && totalTime && (
          <Card size="small" style={{ marginBottom: 16, background: '#f5f5f5' }}>
            <Descriptions size="small" column={4}>
              <Descriptions.Item label="Total Setup Time">
                {totalTime.total_setup_time?.toFixed(2) || 0} hrs
              </Descriptions.Item>
              <Descriptions.Item label="Total Run Time">
                {totalTime.total_run_time?.toFixed(2) || 0} hrs (for {quantity} units)
              </Descriptions.Item>
              <Descriptions.Item label="Total Teardown Time">
                {totalTime.total_teardown_time?.toFixed(2) || 0} hrs
              </Descriptions.Item>
              <Descriptions.Item label="Total Lead Time">
                <Tag color="blue">{totalTime.total_time?.toFixed(2) || 0} hrs</Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {selectedBOM && qualityCheckpoints.length > 0 && (
          <Card size="small" style={{ marginBottom: 16 }} title={`Quality Checkpoints (${qualityCheckpoints.length})`}>
            <Space wrap>
              {qualityCheckpoints.map((cp: BOMOperation, idx: number) => (
                <Tag key={cp.id} color="gold" icon={<CheckCircleOutlined />}>
                  Seq {cp.sequence_number}: {cp.quality_checkpoint_type}
                </Tag>
              ))}
            </Space>
          </Card>
        )}

        <Table
          columns={columns}
          dataSource={routing}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1200 }}
          pagination={false}
          locale={{ emptyText: selectedBOM ? 'No operations in routing' : 'Please select a BOM' }}
        />
      </Card>

      <Modal
        title={editingOperation ? 'Edit Routing Operation' : 'Add Routing Operation'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        width={800}
        confirmLoading={createOperation.isPending || updateOperation.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="bom_id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="operation_id"
            label="Operation"
            rules={[{ required: true, message: 'Please select an operation' }]}
          >
            <Select
              showSearch
              placeholder="Select operation"
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={operations.map((op: any) => ({
                label: `${op.operation_code} - ${op.operation_name}`,
                value: op.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="sequence_number"
            label="Sequence Number"
            rules={[{ required: true, message: 'Please enter sequence number' }]}
          >
            <InputNumber min={1} step={10} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="workstation_id"
            label="Workstation (Optional)"
          >
            <Select
              showSearch
              placeholder="Select workstation"
              allowClear
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={workstations.map((ws: any) => ({
                label: `${ws.workstation_code} - ${ws.workstation_name}`,
                value: ws.id,
              }))}
            />
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              name="setup_time_hours"
              label="Setup Time (hours)"
              rules={[{ required: true }]}
              initialValue={0}
            >
              <InputNumber min={0} step={0.25} style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="run_time_per_unit_hours"
              label="Run Time per Unit (hours)"
              rules={[{ required: true }]}
              initialValue={0}
            >
              <InputNumber min={0} step={0.0001} precision={4} style={{ width: 200 }} />
            </Form.Item>

            <Form.Item
              name="teardown_time_hours"
              label="Teardown Time (hours)"
              rules={[{ required: true }]}
              initialValue={0}
            >
              <InputNumber min={0} step={0.25} style={{ width: 150 }} />
            </Form.Item>
          </Space>

          <Form.Item
            name="batch_size"
            label="Batch Size"
            rules={[{ required: true }]}
            initialValue={1}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="requires_quality_check" label="Requires Quality Check" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.requires_quality_check !== currentValues.requires_quality_check}
          >
            {({ getFieldValue }) =>
              getFieldValue('requires_quality_check') ? (
                <Form.Item name="quality_checkpoint_type" label="Quality Checkpoint Type">
                  <Select placeholder="Select checkpoint type">
                    {Object.values(QualityCheckpointType).map(type => (
                      <Select.Option key={type} value={type}>{type}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item name="description" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional notes..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BOMRoutingPage;

